/**
 * Legibility numbers the composer and the quality checks both read, so the two paths cannot disagree about what is too small or too faint.
 * Pure data and arithmetic, no Babylon, like the rest of `src/layout/`.
 */

/**
 * The type floor, in du (1 du = 1 mm), at {@link REFERENCE_CANVAS_DU}, when no brief states a higher one.
 *
 * The prototype's rule at its standard canvas: its style guides are calibrated for a 1280 du reference canvas, with body type at about
 * 1.5% of the canvas width or more, which on its standard 1280 to 1440 du canvases is 19 to 22 du. It is not 1.5% of each template's own
 * width: on a wide canvas that size overflows the small fixed-size cards a model writes, and the composer does not grow a fixed
 * container its content outgrows.
 */
export const MIN_TEXT_DU = 20;

/** Hierarchy: at most this many distinct type sizes in a unit. The quality check judges it and the blocks finish pass keeps to it. */
export const MAX_TEXT_SIZES = 4;

/** The canvas width, in du, that {@link MIN_TEXT_DU} is calibrated for. */
export const REFERENCE_CANVAS_DU = 1280;

/** WCAG 1.4.3 and 1.4.11 minimum ratios, without rounding before comparison. */
export const WCAG_AA_TEXT_CONTRAST = 4.5;
export const WCAG_AA_NON_TEXT_CONTRAST = 3;

/** WCAG relative luminance of an opaque `#RRGGBB` colour. */
export function relativeLuminance(hex: string): number {
    const channels = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
    const linear = channels.map(s => s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4);
    return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

/** WCAG contrast ratio of two opaque `#RRGGBB` colours, 1 to 21. */
export function contrastRatio(fg: string, bg: string): number {
    const a = relativeLuminance(fg), b = relativeLuminance(bg);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** Black or white, whichever reads better on the opaque `#RRGGBB` background `bg`. The repair both build paths make when a text colour fails. */
export function pickForeground(bg: string): '#000000' | '#ffffff' {
    return contrastRatio('#000000', bg) > contrastRatio('#ffffff', bg) ? '#000000' : '#ffffff';
}
